import axios from 'axios';
import constants from '../constants';
import { Autoclave, AutoclaveType, Frequency, SporetestingResult, SporetestLog, TestKit } from '../views/practice/biological/types';
import { Biobox } from '../types';

const deleteInOfficeSporeTest = async (customerNumber: string, testResultId: number | string) => {
  const response = await axios.delete(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/sporetesting-results/${testResultId}`);
  const deletedTest = response.data;

  return deletedTest;
}

const getBioboxes = async (customerNumber: string) => {
  return await axios
    .get(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/sporetesting-boxes`)
    .then((response) => response?.data);
};

const toggleBioboxActivation = async (customerNumber: string, bioboxId: number) => {
  try {
    const response = await axios.put(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/sporetesting-boxes/${bioboxId}/toggle-active`);
    const updatedBiobox = response.data;

    return updatedBiobox;
  } catch (error) {
    throw error;
  }
}

const getAutoclaves = async (customerNumber: string) => {
  const response = await axios.get(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/sporetesting-autoclaves`)
  const autoclaves: Autoclave[] = response.data

  return autoclaves
};

const getAutoclavesWithConfirmation = async (customerNumber: string) => {
  const response = await axios.get(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/sporetesting-autoclaves?include_confirmation=true&include_bioboxes=true`);
  return response?.data;
};

const getSporetests = async (customerNumber: string) => {
  const response = await axios.get(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/sporetesting-results`)
  const sporetests: SporetestingResult[] = response.data

  return sporetests
};

const createSporeTest = async (customerNumber: string, testResult: Partial<SporetestingResult>) => {
  await axios.post(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/sporetesting-results`, testResult)
}

const updateSporeTest = async (customerNumber: string, testResult: SporetestingResult) => {
  const response = await axios.put(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/sporetesting-results/${testResult.test_result_id}`, testResult)
  const updatedTest = response.data

  return updatedTest
}

const assignBiobox = async (customerNumber: string, autoclaveId: number, boxId: number) => {
  const url = `${constants.ONTRAQ_API_URL}/api2/${customerNumber}/sporetesting-boxes/${boxId}/assign-autoclave`
  const payload = { autoclave_id: autoclaveId }

  const response = await axios.put(url, payload)
  const updatedBox: Biobox = response.data

  return updatedBox
}

const confirmAutoclave = async (customerNumber: string, autoclave: Autoclave, confirmType: string) => {
  const { autoclave_id, autoclavetype_id } = autoclave
  const payload = {
    autoclave_id: autoclave_id,
    confirmed_sterilization_type: autoclavetype_id,
    confirm_type: confirmType,
  }
  await axios.post(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/sporetesting-autoclaves/confirm`, payload)
}

const updateAutoclave = async (customerNumber: string, autoclave: Partial<Autoclave>) => {
  await axios.put(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/sporetesting-autoclaves/${autoclave.autoclave_id}`, autoclave)
}

const deleteAutoclave = async (customerNumber: string, autoclaveId: number) => {
  await axios.delete(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/sporetesting-autoclaves/${autoclaveId}`)
}

const updateKitSterilization = async (customerNumber: string, testKit: TestKit) => {
  const { kitId, sterilizationMethod } = testKit
  const payload = { sterilizationMethod }

  try {
    await axios.put(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/mesa/kit/${kitId}`, payload)
  } catch (error: any) {
    console.error(error)
    const message = 'Failed to update Mesa box sterilization method'
    throw new Error(message)
  }
}

const createAutoclave = async (customerNumber: string, autoclave: Partial<Autoclave>) => {
  await axios.post(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/sporetesting-autoclaves`, autoclave)
}

const unassignBiobox = async (customerNumber: string, boxId: number) => {
  await axios.patch(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/sporetesting-boxes/${boxId}/unassign`)
} 

const getAutoclaveTypes = async (customerNumber: string) => {
  const response = await axios.get(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/sporetesting-autoclave-types`)
  const autoclaveTypes: AutoclaveType[] = response.data

  return autoclaveTypes
}

const getAutoclaveFrequencies = async (customerNumber: string) => {
  const response = await axios.get(`${constants.ONTRAQ_API_URL}/api2/${customerNumber}/sporetesting-autoclave-frequencies`)
  const autoclaveTypes: Frequency[] = response.data

  return autoclaveTypes
}

const getSporetestLogs = async (customerNumber: string, autoclave_id: number | null) => {
  const url = `${constants.ONTRAQ_API_URL}/api2/${customerNumber}/sporetesting-result-logs/${autoclave_id ?? ''}`
  const response = await axios.get(url)
  const sporetestLogs: SporetestLog[] = response.data

  return sporetestLogs
}

const createSporetestLog = async (customerNumber: string, sporetestLog: SporetestLog) => {
  const url = `${constants.ONTRAQ_API_URL}/api2/${customerNumber}/sporetesting-result-logs/`
  const response = await axios.post(url, sporetestLog)
  const { logId } = response.data
  
  return logId
}

export {
  confirmAutoclave,
  deleteInOfficeSporeTest,
  getAutoclaves,
  getAutoclavesWithConfirmation,
  getBioboxes,
  getSporetests,
  getSporetestLogs,
  createSporetestLog,
  assignBiobox,
  toggleBioboxActivation,
  updateSporeTest,
  createSporeTest,
  updateAutoclave,
  deleteAutoclave,
  updateKitSterilization,
  createAutoclave,
  unassignBiobox,
  getAutoclaveTypes,
  getAutoclaveFrequencies,
};
